class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.isOpen = false;
    this._justOpened = false;

    this.setupEventListeners();
    const details = this.closest("details");
    if (details) {
      details.addEventListener("toggle", () => {
        if (details.open) {
          this._justOpened = true;
          setTimeout(() => {
            this.input.focus();
            this._justOpened = false;
          }, 0);
        } else {
          // If details closes within 100ms of opening, ignore this close
          if (this._justOpened) {
            return;
          }
        }
      });
    }
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");
    form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
    const closeButton = this.querySelector(".close__button");
    closeButton.addEventListener("click", this.onCloseButtonClick.bind(this));

    // Auto select input when clicking on header search icon
    document
      .querySelector(".menu-icons .icon-search")
      .addEventListener("click", () => {
        setTimeout(() => {
          this.input.focus();
          this.input.select();
        }, 100); // Delay to prevent the input from being deselected
      });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();
    // Do not close on focus if empty; just return
    if (!searchTerm.length) return;
    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      // Only close if focus leaves the entire dropdown (gaia-dropdown)
      const dropdown = this.closest("gaia-dropdown");
      // Don't close if dropdown is still open and focus is inside
      if (dropdown && !dropdown.contains(document.activeElement)) {
        // Only close if details is not open (prevents race with focus trap)
        const details = this.closest("details");
        if (!details || !details.open) {
          this.close();
        }
      }
    }, 1);
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }

    if (this.isOpen) {
      const focusableElements = this.getFocusableElements();
      const currentIndex = focusableElements.indexOf(document.activeElement);

      if (event.code === "ArrowDown") {
        const nextIndex = (currentIndex + 1) % focusableElements.length;
        focusableElements[nextIndex].focus();
      } else if (event.code === "ArrowUp") {
        const prevIndex =
          (currentIndex - 1 + focusableElements.length) %
          focusableElements.length;
        focusableElements[prevIndex].focus();
      } else if (event.code === "Tab") {
        // Trap focus inside the predictive-search
        event.preventDefault();
        if (event.shiftKey) {
          const prevIndex =
            (currentIndex - 1 + focusableElements.length) %
            focusableElements.length;
          focusableElements[prevIndex].focus();
        } else {
          const nextIndex = (currentIndex + 1) % focusableElements.length;
          focusableElements[nextIndex].focus();
        }
      } else if (event.code === "Escape") {
        // Close the predictive search when Escape is pressed
        this.close(true);
      }
    }
  }

  getFocusableElements() {
    return [
      this.input, // search__input
      this.querySelector("button.search__button"), // search__button
      this.querySelector(".close__button"), // close__button
      ...this.querySelectorAll(".predictive-search__item"), // All predictive-search__item elements
      this.querySelector("#predictive-search-option-search-keywords"), // predictive-search-option-search-keywords
    ].filter((el) => el); // Filter out null elements
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll("li");
    let activeElement = this.querySelector("li");

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement =
        selectedElement.previousElementSibling ||
        allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&${encodeURIComponent("resources[type]")}=product&${encodeURIComponent(
        "resources[limit]"
      )}=4&section_id=predictive-search`
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent
    );
  }

  open() {
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
    this.input.focus();
    this.updateAriaLabel();
  }

  onCloseButtonClick() {
    this.close(true);
    document.body.classList.remove("overflow-hidden");
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.display = "none";
    }

    document.body.classList.remove("overflow-hidden");

    this.isOpen = false;

    const detailsElement = this.closest("details");
    if (detailsElement) {
      detailsElement.open = false;
      // Remove 'open' class if present (for custom CSS)
      detailsElement.classList.remove("open");
      // Blur any focused element inside details to prevent accidental reopening
      if (detailsElement.contains(document.activeElement)) {
        document.activeElement.blur();
      }
    }
    this.updateAriaLabel();
  }

  updateAriaLabel() {
    const detailsElement = this.closest("details");
    if (detailsElement) {
      const summaryElement = detailsElement.querySelector("summary");
      // Get the visible text from the summary (excluding SVGs/icons)
      let summaryText = "";
      summaryElement.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          summaryText += node.textContent.trim();
        }
      });
      if (!summaryText) {
        // Fallback: get all textContent, then trim
        summaryText = summaryElement.textContent.trim();
      }
      if (detailsElement.hasAttribute("open")) {
        summaryElement.setAttribute(
          "aria-label",
          `Close ${summaryText} accordion`
        );
        summaryElement.setAttribute("aria-expanded", "true");
      } else {
        summaryElement.setAttribute(
          "aria-label",
          `Open ${summaryText} accordion`
        );
        summaryElement.setAttribute("aria-expanded", "false");
      }
      // Optionally update detailsElement's aria-label too
      detailsElement.setAttribute(
        "aria-label",
        `${
          detailsElement.hasAttribute("open") ? "Close" : "Open"
        } ${summaryText} accordion`
      );
    }
  }
}

customElements.define("predictive-search", PredictiveSearch);
